import { LOCALES, type Locale } from "@/i18n/types";

export const SITE_ORIGIN = "https://wasok.app";
export const DEFAULT_LOCALE: Locale = "en";

export const SITE_NAME = "WasOK";

/** Relative page paths (no locale prefix). Home is empty string. */
export const PAGE_PATHS = [
  "",
  "/privacy-policy",
  "/terms-of-service",
  "/faq",
  "/support",
  "/licenses",
  "/advertising",
] as const;

export type PagePath = (typeof PAGE_PATHS)[number];

export const OG_IMAGE_PATH = "/assets/images/logo-mark.svg";

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * Build a locale-prefixed path.
 * @param locale
 * @param path - "" for home, or "/faq", "/privacy-policy", etc.
 */
export function localePath(locale: Locale, path: PagePath | string = ""): string {
  const normalized = path === "/" ? "" : path;
  if (!normalized) return `/${locale}`;
  return `/${locale}${normalized.startsWith("/") ? normalized : `/${normalized}`}`;
}

export function absoluteUrl(pathname: string): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return `${SITE_ORIGIN}${path}`;
}

/** Swap the first path segment (locale) while keeping the rest of the path. */
export function replaceLocaleInPathname(
  pathname: string,
  nextLocale: Locale,
): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return `/${nextLocale}`;
  if (isLocale(segments[0])) {
    segments[0] = nextLocale;
  } else {
    segments.unshift(nextLocale);
  }
  return `/${segments.join("/")}`;
}

/** Page path without locale prefix (`""` or `"/faq"`). */
export function pagePathFromPathname(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "";
  if (isLocale(segments[0])) {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "";
  }
  return pathname === "/" ? "" : pathname;
}

export function isLocaleHomePath(pathname: string, locale: Locale): boolean {
  return pathname === `/${locale}` || pathname === `/${locale}/`;
}
